// A range below a width stops this far short of it, so "below 768" ends at 767.98px.
const shy = 0.02;

// A column list or gap holds only these characters, so its value never leaves its declaration.
const safe = /^[\w\s.%()+\-*/,[\]]+$/;

// The keywords align takes, for how cells sit up and down in their rows.
const aligns = ["start", "end", "center", "stretch", "baseline"];

// The keywords justify takes, for how the columns sit across the grid.
const justifies = [
  "start",
  "end",
  "center",
  "stretch",
  "space-between",
  "space-around",
  "space-evenly",
];

/**
 * A whole number at least least, written as a number or as digits, or nothing.
 * @param {unknown} value
 * @param {number} least
 * @returns {number | undefined}
 */
const whole = (value, least) => {
  const n =
    typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
  return typeof n === "number" && Number.isInteger(n) && n >= least
    ? n
    : undefined;
};

/**
 * The page width an entry holds from: 0 when it names none, -1 when what it names is not a width.
 * @param {unknown} from
 * @returns {number}
 */
const fromOf = (from) => {
  if (from === undefined) {
    return 0;
  }
  const px =
    typeof from === "string" && /^\d+(\.\d+)?$/.test(from)
      ? Number(from)
      : from;
  return typeof px === "number" && Number.isFinite(px) && px >= 0 ? px : -1;
};

/**
 * Pixels to a hundredth.
 * @param {number} px
 * @returns {string}
 */
const pixels = (px) => `${Math.round(px * 100) / 100}px`;

/**
 * The media query for page widths from least up to below, or none for every width.
 * @param {number} least
 * @param {number | undefined} below
 * @returns {string}
 */
const range = (least, below) =>
  [
    least > 0 ? `(min-width: ${pixels(least)})` : "",
    below !== undefined ? `(max-width: ${pixels(below - shy)})` : "",
  ]
    .filter(Boolean)
    .join(" and ");

/**
 * Entries in width order, each holding from its own width up to below the next entry's; one entry with no list holds for every width.
 * @template {{ from?: number | string }} T
 * @param {T[] | T | undefined} entries
 * @returns {{ entry: T, least: number, below: number | undefined }[]}
 */
const ranged = (entries) => {
  const list = (Array.isArray(entries) ? entries : entries ? [entries] : [])
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => ({ entry, least: fromOf(entry.from) }))
    .filter(({ least }) => least >= 0)
    .sort((a, b) => a.least - b.least);
  return list.map((item, i) => ({ ...item, below: list[i + 1]?.least }));
};

/**
 * A rule for a selector over a range of widths, or none when it declares nothing.
 * @param {string} selector
 * @param {string[]} declarations
 * @param {number} least
 * @param {number | undefined} below
 * @returns {string}
 */
const rule = (selector, declarations, least = 0, below = undefined) => {
  const body = declarations.filter(Boolean).join(";");
  const media = range(least, below);
  if (body === "") {
    return "";
  }
  return media
    ? `@media ${media}{${selector}{${body}}}`
    : `${selector}{${body}}`;
};

/**
 * A declaration of a value the safe characters write, or none.
 * @param {string} property
 * @param {unknown} value
 * @returns {string}
 */
const declared = (property, value) =>
  typeof value === "string" && safe.test(value.trim())
    ? `${property}:${value.trim()}`
    : "";

/**
 * A declaration of one of the keywords given, or none.
 * @param {string} property
 * @param {unknown} value
 * @param {string[]} keywords
 * @returns {string}
 */
const keyword = (property, value, keywords) =>
  typeof value === "string" && keywords.includes(value)
    ? `${property}:${value}`
    : "";

/**
 * Where a cell sits: its column and how many it spans, its row, and its layer.
 * @param {import("./definition").cell} cell
 * @returns {string[]}
 */
const placed = (cell) => {
  const col = whole(cell.col, 1);
  const span = whole(cell.span, 1);
  const row = whole(cell.row, 1);
  const layer = whole(cell.layer, 0);
  return [
    col !== undefined || span !== undefined
      ? `grid-column:${col ?? "auto"}${span !== undefined ? ` / span ${span}` : ""}`
      : "",
    row !== undefined ? `grid-row:${row}` : "",
    layer !== undefined ? `z-index:${layer}` : "",
  ];
};

const grid = {
  /**
   * The class a grid's child at index wears for its cell.
   * @param {string} scope - the grid's own class
   * @param {number} index
   * @returns {string}
   */
  cellClass: (scope, index) => `${scope}-${index}`,

  /**
   * The style a grid draws: its columns and gaps per width, its alignment, and each child's cell per width, all under its own class.
   * @param {string} scope - the grid's own class
   * @param {import("./definition").properties | undefined} props
   * @param {import("./definition").definition[]} kids
   * @returns {string}
   */
  styleOf: (scope, props, kids) =>
    [
      // Zero weight, so a class the grid wears that hides it still wins.
      `:where(.${scope}){display:grid}`,
      rule(`.${scope}`, [
        keyword("align-items", props?.align, aligns),
        keyword("justify-content", props?.justify, justifies),
      ]),
      ...ranged(props?.cols).map(({ entry, least, below }) =>
        rule(
          `.${scope}`,
          [
            declared("grid-template-columns", entry.cols),
            declared("gap", entry.gap),
          ],
          least,
          below,
        ),
      ),
      ...kids.flatMap((kid, index) =>
        ranged(kid?.p?.cell).map(({ entry, least, below }) =>
          rule(`.${grid.cellClass(scope, index)}`, placed(entry), least, below),
        ),
      ),
    ]
      .filter(Boolean)
      .join("\n"),
};

export default grid;
